/**
 * SSOT — סה"כ הצעת מחיר מלון לתצוגה/PDF/תשלום (בסיס + עמלת סוכן + תוספות מקושרות).
 */

import {
  isHotelApiPriceOnlyOrder,
  PRICE_PIPELINE_MODE_HOTEL_API_ONLY,
} from "./hotelApiPriceOnlyPipeline";
import { resolveCustomerFacingHotelLineIlsFromOrder } from "./hotelBookingPriceSsot";
import { formatHotelIlsRoundedDisplay } from "./formatHotelIlsRoundedDisplay";
import {
  buildLockedHotelDisplayCurrenciesFromSnapshot,
  buildLockedUsdToIlsFn,
  resolveHotelBookingSummaryPricingSsot,
} from "./resolveHotelBookingSummaryPricingSsot";

function roundIls(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.ceil(x);
}

/** @param {unknown} displayCurrencies */
export function parsePackageDisplayUsd(displayCurrencies) {
  if (!displayCurrencies || typeof displayCurrencies !== "object") return null;
  const raw = displayCurrencies.usd;
  if (raw != null && raw !== "") {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const fmt = displayCurrencies.formattedUsd;
  if (fmt == null || String(fmt).trim() === "") return null;
  const m = String(fmt).replace(/[^\d.,]/g, "").replace(/,/g, "");
  const n = Number(m);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * @param {object|null|undefined} ourPrice
 * @param {number|null} baseIls
 * @param {(usd: number) => number} [usdToIls]
 */
export function resolveHotelAgentHandlingFeeIls(ourPrice, baseIls, usdToIls) {
  const handleFeeData = ourPrice?.handleFeeData;
  const fixedCommissionIls = ourPrice?.fixedCommissionInputIls;

  if (handleFeeData?.type === "%") {
    const pct = Number(handleFeeData.value);
    const base = Number(baseIls);
    if (!Number.isFinite(pct) || pct <= 0 || !Number.isFinite(base) || base <= 0) {
      return 0;
    }
    return Math.round((base * pct) / 100);
  }

  if (handleFeeData?.type === "+") {
    const hfUsd = Number(ourPrice?.handlingFee);
    if (fixedCommissionIls != null && Number.isFinite(Number(fixedCommissionIls))) {
      const fixedIls = Math.round(Number(fixedCommissionIls));
      if (fixedIls > 0) {
        if (
          Number.isFinite(hfUsd) &&
          hfUsd > 0 &&
          Math.abs(fixedIls - hfUsd) < 0.02 &&
          typeof usdToIls === "function"
        ) {
          return roundIls(usdToIls(hfUsd));
        }
        return fixedIls;
      }
    }
    if (Number.isFinite(hfUsd) && hfUsd > 0 && typeof usdToIls === "function") {
      return roundIls(usdToIls(hfUsd));
    }
    const valUsd = Number(handleFeeData.value);
    if (Number.isFinite(valUsd) && valUsd > 0 && typeof usdToIls === "function") {
      return roundIls(usdToIls(valUsd));
    }
  }

  if (fixedCommissionIls != null && Number.isFinite(Number(fixedCommissionIls))) {
    const n = Math.round(Number(fixedCommissionIls));
    if (n > 0) return n;
  }

  return 0;
}

/**
 * @param {{
 *   currentHotelOrder: object|null,
 *   calcOurTotalPrice?: (order: object) => object|null,
 *   usdToIls?: (usd: number, fixed?: boolean) => number,
 *   ilsToUsd?: (ils: number) => number,
 *   linkedFlightsDealPrice?: number,
 *   supplementsIls?: number,
 * }} opts
 */
export function resolveHotelAgentOfferDisplayTotal(opts) {
  const order = opts.currentHotelOrder;
  const usdToIls = opts.usdToIls;
  const linkedFlights = Number(opts.linkedFlightsDealPrice) || 0;
  const supplementsIls = Number(opts.supplementsIls) || 0;

  const summarySsot = resolveHotelBookingSummaryPricingSsot({
    currentHotelOrder: order,
    calcOurTotalPrice: opts.calcOurTotalPrice,
    usdToIls,
    sourcePath: `resolveHotelAgentOfferDisplayTotal|${opts.sourcePath || "unknown"}`,
    trace: false,
  });

  const ourPrice = order?.ourPrice || order?.selectedHotelPackage?.ourPrice || {};
  const displayCurrencies = order?.selectedHotelPackage?.displayCurrencies;
  const lockedSnap = summarySsot.pricingSnapshot;
  const lockedUsdToIls = buildLockedUsdToIlsFn(lockedSnap, usdToIls);

  let baseIls = summarySsot.ok ? summarySsot.hotelTotalIls : null;
  let baseUsd = null;
  let sourcePath = lockedSnap
    ? `pricingSnapshot|${summarySsot.sourcePath}`
    : "unknown";

  if (lockedSnap?.hotelTotalIls != null) {
    baseIls = roundIls(lockedSnap.hotelTotalIls);
    const orig = lockedSnap.hotelTotalOriginalCurrency;
    if (orig != null && Number.isFinite(Number(orig)) && Number(orig) > 0) {
      baseUsd = Number(orig);
    }
    sourcePath = `${sourcePath}|locked_snapshot`;
  } else if (isHotelApiPriceOnlyOrder(order)) {
    baseIls = resolveCustomerFacingHotelLineIlsFromOrder(order);
    baseUsd = parsePackageDisplayUsd(displayCurrencies);
    sourcePath = "bookingSsotIls+displayCurrencies.usd";
  } else if (typeof opts.calcOurTotalPrice === "function" && order) {
    const priced = opts.calcOurTotalPrice(order);
    const commissionUsd = Number(priced?.priceWithCommission);
    if (Number.isFinite(commissionUsd) && typeof lockedUsdToIls === "function") {
      baseIls = roundIls(lockedUsdToIls(commissionUsd));
      sourcePath = "calcOurTotalPrice.priceWithCommission→lockedUsdToIls";
    }
    baseUsd =
      parsePackageDisplayUsd(displayCurrencies) ??
      (typeof opts.ilsToUsd === "function" && baseIls != null
        ? Number(opts.ilsToUsd(baseIls))
        : null);
  }

  if (baseIls == null) {
    baseIls = resolveCustomerFacingHotelLineIlsFromOrder(order);
    sourcePath = `${sourcePath}|fallback.bookingSsotIls`;
  }
  if (baseUsd == null && baseIls != null && typeof opts.ilsToUsd === "function") {
    const u = Number(opts.ilsToUsd(baseIls));
    if (Number.isFinite(u) && u > 0) baseUsd = u;
  }

  const agentFeeIls =
    summarySsot.ok && summarySsot.agentFeeIls != null
      ? summarySsot.agentFeeIls
      : resolveHotelAgentHandlingFeeIls(ourPrice, baseIls, lockedUsdToIls);
  let exchangeRate =
    summarySsot.lockedExchangeRateToIls != null
      ? summarySsot.lockedExchangeRateToIls
      : null;
  if (exchangeRate == null && baseIls != null && baseIls > 0 && baseUsd != null && baseUsd > 0) {
    exchangeRate = baseIls / baseUsd;
  }

  const hotelBeforeFeeIls = baseIls ?? 0;
  const hotelAfterFeeIls = roundIls(hotelBeforeFeeIls + agentFeeIls);

  let agentFeeUsd = 0;
  let hotelAfterFeeUsd = baseUsd;
  if (baseUsd != null && baseUsd > 0) {
    if (exchangeRate != null && exchangeRate > 0) {
      agentFeeUsd = agentFeeIls / exchangeRate;
      hotelAfterFeeUsd = baseUsd + agentFeeUsd;
    } else if (typeof opts.ilsToUsd === "function" && agentFeeIls > 0) {
      agentFeeUsd = Number(opts.ilsToUsd(agentFeeIls)) || 0;
      hotelAfterFeeUsd = baseUsd + agentFeeUsd;
    }
  }

  const totalBeforeFeeIls = roundIls(hotelBeforeFeeIls + linkedFlights + supplementsIls);
  const totalAfterFeeIls = roundIls(hotelAfterFeeIls + linkedFlights + supplementsIls);

  const formattedUsd =
    hotelAfterFeeUsd != null && Number.isFinite(hotelAfterFeeUsd) && hotelAfterFeeUsd > 0
      ? `($${hotelAfterFeeUsd.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })})`
      : displayCurrencies?.formattedUsd ?? null;

  const mergedDisplayCurrencies =
    buildLockedHotelDisplayCurrenciesFromSnapshot(lockedSnap, null) ??
    (displayCurrencies && typeof displayCurrencies === "object"
      ? {
          ...displayCurrencies,
          ils: hotelAfterFeeIls,
          formattedIls:
            formatHotelIlsRoundedDisplay(hotelAfterFeeIls) ?? `₪ ${hotelAfterFeeIls}`,
          hasUsd: formattedUsd != null,
          formattedUsd,
          usd: hotelAfterFeeUsd ?? displayCurrencies.usd,
        }
      : null);

  return {
    pipelineMode: order?.pricePipelineMode ?? null,
    baseIls: hotelBeforeFeeIls,
    baseUsd,
    agentFeeIls,
    agentFeeUsd,
    exchangeRate,
    totalBeforeFeeIls,
    totalAfterFeeIls,
    displayedIls: totalAfterFeeIls,
    displayedUsd: hotelAfterFeeUsd,
    displayCurrencies: mergedDisplayCurrencies,
    sourcePath,
    isApiPriceOnly: order?.pricePipelineMode === PRICE_PIPELINE_MODE_HOTEL_API_ONLY,
  };
}

/** @param {Record<string, unknown>} payload */
export function logHotelAgentFeeTotalRecalcTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  // eslint-disable-next-line no-console
  console.info("[HOTEL_AGENT_FEE_TOTAL_RECALC_TRACE]", payload);
}
